import {
  AirChannels,
  AirHandoffReasons,
  AirIntents,
  AirOutcomes,
  SchedulingOpportunity,
  TaskStatusFilter,
  TaskTypeFilter,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { i18next } from '@frontend/i18n';

export const APPOINTMENT_SCHEDULED_CHART_APPEARANCE = {
  borderRadius: 8,
  height: 190,
  innerRadius: 70,
  minWidth: 180,
  outerRadius: 94,
};

export const FOLLOW_UP_TASKS_CHART_APPEARANCE = {
  barSize: 60,
  groupsGap: 60,
  showYAxis: true,
  showXAxis: true,
  skipBarRadiusOnStackJoins: true,
  mode: 'stacked' as const,
  barBorderWidth: 1,
  alwaysApplyBarBorders: true,
  margin: { left: -16 },
};

export const SCHEDULING_OPPORTUNITIES_CHART_APPEARANCE = {
  barSize: 40,
  groupsGap: 24,
  layout: 'vertical' as const,
  showYAxis: false,
  margin: { top: 0, right: 8, left: 16, bottom: 0 },
  hideTooltip: false,
  barBorderWidth: 1,
  alwaysApplyBarBorders: true,
};

export enum MAirOutcomes {
  SCHEDULE = 'SCHEDULE',
  RESCHEDULE = 'RESCHEDULE',
  SCHEDULE_REQUEST = 'SCHEDULE_REQUEST',
}

export const LabelMap: Record<string, string> = {
  Scheduled: i18next.t('Scheduled', { ns: 'analytics' }),
  Rescheduled: i18next.t('Rescheduled', { ns: 'analytics' }),
  Staff_Answered: i18next.t('Staff Answered', { ns: 'analytics' }),
  Staff_Unanswered: i18next.t('Staff Unanswered', { ns: 'analytics' }),
  Unscheduled: i18next.t('Unscheduled', { ns: 'analytics' }),
  Recovered_Opportunity: i18next.t('Recovered Opportunity', { ns: 'analytics' }),
  Scheduling: i18next.t('Scheduling', { ns: 'analytics' }),
  Other: i18next.t('Other', { ns: 'analytics' }),
  Total_Ai_Conversations: i18next.t('Total AI Conversations', { ns: 'analytics' }),
  Staff_Transfer_Messages: i18next.t('AI-Initiated Staff Transfer Messages', { ns: 'analytics' }),
  appointmentScheduled: i18next.t('New Appointments', { ns: 'analytics' }),
  appointmentRescheduled: i18next.t('Rescheduled', { ns: 'analytics' }),

  // Intents
  [AirIntents.SESSION_INTENT_UNSPECIFIED]: i18next.t('Other', { ns: 'analytics' }),
  [AirIntents.SESSION_INTENT_SERVICE_IDENTIFICATION]: i18next.t('FAQ', { ns: 'analytics' }),
  [AirIntents.SESSION_INTENT_PROPOSE_AVAILABILITY]: i18next.t('Scheduling Availability', { ns: 'analytics' }),
  [AirIntents.SESSION_INTENT_USER_INFORMATION_COLLECTION]: i18next.t('Other', { ns: 'analytics' }),
  [AirIntents.SESSION_INTENT_BOOKING_REQUESTED]: i18next.t('New Booking', { ns: 'analytics' }),
  [AirIntents.SESSION_INTENT_RESCHEDULING_REQUESTED]: i18next.t('Rescheduling', { ns: 'analytics' }),
  [AirIntents.SESSION_INTENT_CANCELLATION_REQUESTED]: i18next.t('Cancellation', { ns: 'analytics' }),

  // Handoff Reasons
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_UNSPECIFIED]: i18next.t('Other', { ns: 'analytics' }),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_LANDLINE_CALL]: i18next.t('Landline Call', { ns: 'analytics' }),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_UNRESPONSIVE_VOICEMAIL]: i18next.t('Customer Unresponsive', {
    ns: 'analytics',
  }),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_CC_COLLECT]: i18next.t('Payment', { ns: 'analytics' }),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_CC_ADD_FAILURE]: i18next.t('Payment', { ns: 'analytics' }),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_BOOKING_DISALLOWED]: i18next.t('Scheduling Action not allowed', {
    ns: 'analytics',
  }),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_CONFIRM_BOOKING]: i18next.t('Appointment status confirmation', {
    ns: 'analytics',
  }),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_CONFIRM_RESCHEDULE]: i18next.t('Appointment status confirmation', {
    ns: 'analytics',
  }),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_CONFIRM_CANCEL]: i18next.t('Appointment status confirmation', {
    ns: 'analytics',
  }),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_CALLBACK_CUSTOMER]: i18next.t('Customer Requested staff', {
    ns: 'analytics',
  }),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_RUNNING_LATE]: i18next.t('Other', { ns: 'analytics' }),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_BOOKING_NOT_FOUND]: i18next.t('Appointment not found', {
    ns: 'analytics',
  }),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_SIGNUP_FOR_CLASS]: i18next.t('Scheduling Action not allowed', {
    ns: 'analytics',
  }),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_BOOKING_FORWARD]: i18next.t('Requested Service not available', {
    ns: 'analytics',
  }),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_RESCHEDULE_FORWARD]: i18next.t('Requested Service not available', {
    ns: 'analytics',
  }),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_CANCEL_FORWARD]: i18next.t('Requested Service not available', {
    ns: 'analytics',
  }),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_PRE_BOOKING_FORWARD]: i18next.t('Requested Service not available', {
    ns: 'analytics',
  }),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_SERVICE_STAFF_NOT_FOUND]: i18next.t(
    'Requested scheduling criteria not available',
    {
      ns: 'analytics',
    }
  ),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_BILLING_RELATED]: i18next.t('Billing related', { ns: 'analytics' }),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_CANCELLATION_WAITLIST]: i18next.t('Scheduling Action not allowed', {
    ns: 'analytics',
  }),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_CUSTOMER_UNRESPONSIVE]: i18next.t('Customer Unresponsive', {
    ns: 'analytics',
  }),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_MEMBERSHIP_CANCELLATION_REQUESTED]: i18next.t('Cancellation', {
    ns: 'analytics',
  }),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_NO_AVAILABILITY_FOUND]: i18next.t(
    'Requested scheduling criteria not available',
    {
      ns: 'analytics',
    }
  ),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_FAILED_LATE_CANCEL_FEE]: i18next.t('Action not allowed', {
    ns: 'analytics',
  }),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_BOOKING_HELP_NEEDED]: i18next.t(
    'Requested scheduling criteria not available',
    {
      ns: 'analytics',
    }
  ),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_RESCHEDULE_HELP_NEEDED]: i18next.t(
    'Requested scheduling criteria not available',
    {
      ns: 'analytics',
    }
  ),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_CANCEL_HELP_NEEDED]: i18next.t(
    'Requested scheduling criteria not available',
    {
      ns: 'analytics',
    }
  ),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_BOOKING_FAILURE]: i18next.t('Scheduling System issue', {
    ns: 'analytics',
  }),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_CONFIRMATION_FAILURE]: i18next.t('Scheduling System issue', {
    ns: 'analytics',
  }),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_INFORMATION_CHANGE]: i18next.t('Customer Requested staff', {
    ns: 'analytics',
  }),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_DATE_OUT_OF_RANGE]: i18next.t(
    'Requested scheduling criteria not available',
    {
      ns: 'analytics',
    }
  ),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_INSURANCE_SUBMISSION_FAILED]: i18next.t('Insurance', { ns: 'analytics' }),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_OTHER]: i18next.t('Other', { ns: 'analytics' }),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_LEAD_ENGAGE_OTHER_REQUEST]: i18next.t(
    'Requested scheduling criteria not available',
    {
      ns: 'analytics',
    }
  ),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_LEAD_ENGAGE_BLOCKED_SERVICE]: i18next.t('Action not allowed', {
    ns: 'analytics',
  }),
  [AirHandoffReasons.SESSION_HAND_OFF_REASON_EXPIRED_CAMPAIGN]: i18next.t('Expired campaign', { ns: 'analytics' }),
  [AirHandoffReasons.SESSION_HANDLED_BY_AI]: i18next.t('No-hand off', { ns: 'analytics' }),
  [AirHandoffReasons.SESSION_HAND_OFF_TO_STAFF]: i18next.t('Staff hand-off', { ns: 'analytics' }),

  // Outcomes
  [AirOutcomes.SESSION_OUTCOME_UNSPECIFIED]: i18next.t('Other', { ns: 'analytics' }),
  [AirOutcomes.SESSION_OUTCOME_BOOKING_COMPLETION]: i18next.t('New Booking', { ns: 'analytics' }),
  [AirOutcomes.SESSION_OUTCOME_CANCELLATION_COMPLETION]: i18next.t('Appointment Cancellation', { ns: 'analytics' }),
  [AirOutcomes.SESSION_OUTCOME_RESCHEDULING_COMPLETION]: i18next.t('Reschedule', { ns: 'analytics' }),
  [AirOutcomes.SESSION_OUTCOME_FAQ_ASSISTANCE_COMPLETE]: i18next.t('FAQ answered', { ns: 'analytics' }),
  [AirOutcomes.THREAD_AI_OUTCOME_APPOINTMENT_REQUEST]: i18next.t('Schedule Request', { ns: 'analytics' }),

  // Channels
  [AirChannels.SESSION_INITIATED_FOR_UNSPECIFIED]: i18next.t('Other', { ns: 'analytics' }),
  [AirChannels.SESSION_INITIATED_FOR_MISSED_CALL]: i18next.t('Missed Call Text', { ns: 'analytics' }),
  [AirChannels.SESSION_INITIATED_FOR_OUTREACH]: i18next.t('Outbound', { ns: 'analytics' }),
  [AirChannels.SESSION_INITIATED_FOR_DIRECT_MESSAGE]: i18next.t('Direct Text', { ns: 'analytics' }),
  [AirChannels.SESSION_INITIATED_FOR_WEB_TO_TEXT]: i18next.t('Web to text', { ns: 'analytics' }),
  [AirChannels.SESSION_INITIATED_FOR_WEB_STORE]: i18next.t('Web chat', { ns: 'analytics' }),
  [AirChannels.SESSION_INITIATED_FOR_WEB_CHAT]: i18next.t('Web chat', { ns: 'analytics' }),
  [AirChannels.SESSION_INITIATED_FOR_APPOINTMENT_REMINDER]: i18next.t('Appointment reminder', { ns: 'analytics' }),
  [AirChannels.SESSION_INITIATED_FOR_ABANDONED_CALL]: i18next.t('Other', { ns: 'analytics' }),
  [AirChannels.SESSION_INITIATED_FOR_PARTNER_API]: i18next.t('Other', { ns: 'analytics' }),
  [AirChannels.SESSION_INITIATED_FOR_VOICE_CALL]: i18next.t('Other', { ns: 'analytics' }),
  [AirChannels.SESSION_INITIATED_FOR_INCOMING_VOICE_CALL]: i18next.t('Other', { ns: 'analytics' }),

  // Task Status
  [TaskStatusFilter.TO_DO]: i18next.t('To Do', { ns: 'analytics' }),
  [TaskStatusFilter.PENDING]: i18next.t('Pending', { ns: 'analytics' }),
  [TaskStatusFilter.COMPLETED]: i18next.t('Completed', { ns: 'analytics' }),
  [TaskStatusFilter.NO_TASK]: i18next.t('No Task', { ns: 'analytics' }),
  [TaskStatusFilter.OTHER]: i18next.t('Other', { ns: 'analytics' }),

  // Task Type
  [TaskTypeFilter.TYPE_SCHEDULING]: i18next.t('Scheduling', { ns: 'analytics' }),
  [TaskTypeFilter.TYPE_WAITLIST]: i18next.t('Waitlist', { ns: 'analytics' }),
  [TaskTypeFilter.TYPE_PATIENT_CARE]: i18next.t('Patient Care', { ns: 'analytics' }),
  [TaskTypeFilter.TYPE_INSURANCE]: i18next.t('Insurance', { ns: 'analytics' }),
  [TaskTypeFilter.TYPE_BILLING]: i18next.t('Billing', { ns: 'analytics' }),
  [TaskTypeFilter.TYPE_OTHER]: i18next.t('Other', { ns: 'analytics' }),

  // Office Hours
  OFFICE_STATUS_OPEN: i18next.t('Open', { ns: 'analytics' }),
  OFFICE_STATUS_CLOSED: i18next.t('Closed', { ns: 'analytics' }),
  OFFICE_STATUS_OTHER: i18next.t('Other', { ns: 'analytics' }),
  OFFICE_STATUS_UNSPECIFIED: i18next.t('Other', { ns: 'analytics' }),

  // Contact Type
  unknown: i18next.t('Unknown Contact', { ns: 'analytics' }),
  current_patient: i18next.t('Existing Patient', { ns: 'analytics' }),

  // Scheduling Opportunities
  [SchedulingOpportunity.ALL_OPPORTUNITIES]: i18next.t('All', { ns: 'analytics' }),
  [SchedulingOpportunity.SCHEDULED_BY_AI]: i18next.t('Scheduled by AI', { ns: 'analytics' }),
  [SchedulingOpportunity.RECOVERED_OPPORTUNITIES]: i18next.t('Recovered Opportunities', { ns: 'analytics' }),
  [SchedulingOpportunity.UNSCHEDULED_OPPORTUNITIES]: i18next.t('Unscheduled Opportunities', { ns: 'analytics' }),
  [SchedulingOpportunity.NOT_APPLICABLE]: i18next.t('Not Applicable', { ns: 'analytics' }),

  // Custom MAIR Outcomes
  [MAirOutcomes.SCHEDULE]: i18next.t('Scheduled', { ns: 'analytics' }),
  [MAirOutcomes.RESCHEDULE]: i18next.t('Rescheduled', { ns: 'analytics' }),
  [MAirOutcomes.SCHEDULE_REQUEST]: i18next.t('Schedule Request', { ns: 'analytics' }),
};

export enum LABEL_KEYS {
  VOICE = 'Voice',
  MESSAGE = 'Message',
}

export enum VIEW_KEYS {
  CHART = 'chart',
  TABLE = 'table',
}

export const VIEW_SWITCHER_TOOLTIP_COPY: Record<VIEW_KEYS, string> = {
  [VIEW_KEYS.CHART]: i18next.t('Switch to Chart view from here.', { ns: 'analytics' }),
  [VIEW_KEYS.TABLE]: i18next.t('Switch to Table view from here.', { ns: 'analytics' }),
};

export const AIR_FEATURE_FLAGS = {
  DEMO_MODE: 'analytics-air-demo-mode',
};
